// useSystemSwitches — runtime on/off state for the three non-Flow-Weaver
// subsystems (AI, Atlas, Process Engine). Backed by 3 rows in
// app_settings; admin toggles them via admin_set_system_switch RPC.
//
// IMPORTANT: this is the RUNTIME layer. The compile-time build flags
// (IS_ATLAS_BUILD, IS_PROCESS_ENGINE_BUILD) STILL take precedence —
// effective-enabled = build-on AND runtime-on. Capability hooks
// compose both in Fas 6.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fromCustom, callRpc } from '@/lib/supabaseTyped';

export type SystemKey = 'system.ai' | 'system.atlas' | 'system.process_engine';

interface AppSettingRow {
  key: string;
  value: { enabled?: boolean } | null;
}

export interface SystemSwitches {
  ai: boolean;
  atlas: boolean;
  processEngine: boolean;
}

const QUERY_KEY = ['app_settings', 'system_switches'] as const;
const SWITCH_KEYS: SystemKey[] = ['system.ai', 'system.atlas', 'system.process_engine'];

function defaultSwitches(): SystemSwitches {
  return { ai: true, atlas: true, processEngine: true };
}

function rowsToSwitches(rows: AppSettingRow[]): SystemSwitches {
  const out = defaultSwitches();
  for (const row of rows) {
    if (row.key === 'system.ai') out.ai = row.value?.enabled !== false;
    else if (row.key === 'system.atlas') out.atlas = row.value?.enabled !== false;
    else if (row.key === 'system.process_engine') out.processEngine = row.value?.enabled !== false;
  }
  return out;
}

/**
 * Read-only hook for capability code paths. Defaults to all-on when
 * the rows are missing or the query is loading — fail-open is the
 * right choice here (avoids "site died because Supabase glitched").
 */
export function useSystemSwitches(): {
  switches: SystemSwitches;
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<AppSettingRow[]> => {
      const { data, error } = await (fromCustom<AppSettingRow>('app_settings')
        .select('key, value')
        .in('key', SWITCH_KEYS) as unknown as Promise<{ data: AppSettingRow[] | null; error: Error | null }>);

      if (error) throw error;
      return (data ?? []) as AppSettingRow[];
    },
    staleTime: 30_000,
  });

  return {
    switches: q.data ? rowsToSwitches(q.data) : defaultSwitches(),
    isLoading: q.isLoading,
  };
}

/**
 * Admin mutation for the SystemSwitchesPanel.
 */
export function useSystemSwitchMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { key: SystemKey; enabled: boolean }): Promise<void> => {
      const { error } = await callRpc<unknown>('admin_set_system_switch', {
        _key: args.key,
        _enabled: args.enabled,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
